export default function couponHandler() {
  // Debug flag - can be set to true or injected from WP configuration
  const debug = window.WP_DEBUG || false;

  // Logger function to handle debug mode
  const log = {
    info: (message, data) => {
      if (debug) console.log(`🔵 Coupon: ${message}`, data || "");
    },
    warn: (message, data) => {
      if (debug) console.warn(`🟠 Coupon: ${message}`, data || "");
    },
    error: (message, data) => {
      console.error(`🔴 Coupon: ${message}`, data || "");
    },
  };

  log.info("Initializing coupon handler");

  // Get DOM elements
  const couponInput = document.getElementById("coupon_code");
  const applyButton = document.getElementById("apply_coupon");
  const couponMessage = document.querySelector(".coupon-message");
  const appliedCoupons = document.querySelector(".applied-coupons");
  const couponFormContainer = document.querySelector(".coupon-form-container");

  // Check if required elements exist
  if (!couponInput || !applyButton) {
    log.warn("Required coupon elements not found in DOM");
    return;
  }

  log.info("Found all required coupon elements in DOM");

  // Track applied coupons
  const appliedCouponCodes = new Set();

  // More aggressive coupon clearing check
  const shouldClearCouponData = () => {
    // Check both the order_completed flag and checkout_in_progress flag
    return (
      sessionStorage.getItem("order_completed") === "true" ||
      // Also check URL parameters that might indicate a post-order state
      window.location.search.includes("order_complete=true") ||
      window.location.search.includes("order_id=") ||
      // Check for referrer from payment response or order confirmation pages
      (document.referrer &&
        (document.referrer.includes("payment-response") ||
          document.referrer.includes("order-received")))
    );
  };

  // Clear coupons if we detect we're coming back after an order
  if (shouldClearCouponData()) {
    log.info("Post-order state detected, clearing all coupon storage");
    sessionStorage.removeItem("checkout_applied_coupons");
    sessionStorage.removeItem("checkout_page_visited");
    sessionStorage.removeItem("order_completed");
    sessionStorage.removeItem("checkout_in_progress");

    // Also make an AJAX call to clear server-side coupons
    clearServerSideCoupons();
  }

  // Initialize order totals when the page loads
  fetchAndRenderOrderTotals();

  // Set up coupon input and apply button behavior
  // Enable/disable apply button based on input
  couponInput.addEventListener("input", function () {
    const value = couponInput.value.trim();
    applyButton.disabled = !value;
    log.info(`Coupon input changed: ${value ? value : "(empty)"}`);
  });

  // Initialize button state on page load
  applyButton.disabled = !couponInput.value.trim();

  // Allow enter key to submit the coupon
  couponInput.addEventListener("keypress", function (e) {
    if (e.key === "Enter" && couponInput.value.trim()) {
      e.preventDefault();
      log.info("Enter key pressed on coupon input, submitting form");
      applyButton.click();
    }
  });

  // Check existing coupons in the DOM and add them to our tracking Set
  function initializeAppliedCoupons() {
    const existingCoupons = document.querySelectorAll(
      ".applied-coupon .coupon-code"
    );
    log.info(`Found ${existingCoupons.length} existing coupons`);

    existingCoupons.forEach((coupon) => {
      const code = coupon.textContent.trim().toLowerCase();
      appliedCouponCodes.add(code);
      log.info(`Added existing coupon to tracking: ${code}`);
    });
  }

  // Initialize remove button event listeners for existing coupon elements
  function initializeRemoveButtons() {
    const removeButtons = document.querySelectorAll(
      ".applied-coupon .remove-coupon"
    );
    log.info(`Found ${removeButtons.length} existing remove buttons`);

    removeButtons.forEach((button) => {
      const code = button.getAttribute("data-coupon");
      const couponElement = button.closest(".applied-coupon");

      if (code && couponElement) {
        button.addEventListener("click", function () {
          log.info(`Remove button clicked for existing coupon: ${code}`);
          removeCoupon(code, couponElement);
        });
        log.info(`Added event listener to remove button for coupon: ${code}`);
      }
    });
  }

  // Initialize existing coupons on page load
  initializeAppliedCoupons();
  // Initialize remove buttons for existing coupons
  initializeRemoveButtons();

  // Function for displaying coupon messages
  function showCouponMessage(message, type = "error") {
    if (!couponMessage) {
      log.warn("Coupon message container not found");
      return;
    }

    log.info(`Displaying message: ${message} (${type})`);
    couponMessage.textContent = message;
    couponMessage.className = "coupon-message";
    couponMessage.classList.add(type);

    // Automatically hide success messages after 4 seconds
    if (type === "success") {
      setTimeout(() => {
        log.info("Auto-clearing success message");
        couponMessage.textContent = "";
        couponMessage.className = "coupon-message";
      }, 4000);
    }
  }

  // Function for displaying applied coupons
  function displayAppliedCoupon(code) {
    if (!appliedCoupons) {
      log.warn("Applied coupons container not found");
      return;
    }

    // Add to tracking Set
    appliedCouponCodes.add(code.toLowerCase());
    log.info(`Adding coupon to UI: ${code}`);

    // Create coupon element
    const couponElement = document.createElement("div");
    couponElement.className = "applied-coupon";
    couponElement.innerHTML = `
      <span class="coupon-code">${code}</span>
      <button type="button" class="remove-coupon" data-coupon="${code}">×</button>
    `;

    // Add to applied coupons container
    appliedCoupons.appendChild(couponElement);
    log.info(`Coupon element added to DOM`);

    // Clear input field
    if (couponInput) {
      couponInput.value = "";
      applyButton.disabled = true;
    }

    // Add event listener for remove button
    const removeButton = couponElement.querySelector(".remove-coupon");
    if (removeButton) {
      removeButton.addEventListener("click", function () {
        log.info(`Remove button clicked for coupon: ${code}`);
        removeCoupon(code, couponElement);
      });
    }
  }

  // Apply coupon via AJAX
  async function applyCoupon(code) {
    log.info(`Starting apply coupon request for: ${code}`);

    try {
      // Disable the button and show loading state
      applyButton.disabled = true;
      const originalText = applyButton.textContent;
      applyButton.textContent = "...";
      log.info("Button disabled, showing loading state");

      const formData = new FormData();
      formData.append("action", "apply_coupon");
      formData.append("security", couponVars.nonce);
      formData.append("coupon_code", code);

      log.info("Sending AJAX request to apply coupon", {
        url: couponVars.ajaxUrl,
        code: code,
        nonce: couponVars.nonce ? "Valid nonce present" : "No valid nonce",
      });

      const response = await fetch(couponVars.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: new URLSearchParams(formData),
      });

      log.info(`AJAX response received, status: ${response.status}`);
      const data = await response.json();
      log.info("AJAX response data:", data);

      // Reset button state
      applyButton.disabled = false;
      applyButton.textContent = originalText;
      log.info("Reset button state");

      if (data.success) {
        log.info(
          `Coupon applied successfully: ${code}, discount: ${data.data.discount_raw}`
        );
        showCouponMessage(data.data.message, "success");
        displayAppliedCoupon(code);

        // Fetch updated order totals
        fetchAndRenderOrderTotals();
      } else {
        log.error(
          `Failed to apply coupon: ${
            data.data ? data.data.message : "Unknown error"
          }`
        );
        showCouponMessage(
          data.data?.message || couponVars.messages.error,
          "error"
        );
      }
    } catch (error) {
      log.error("Error applying coupon:", error);
      applyButton.disabled = false;
      applyButton.textContent = originalText;
      showCouponMessage(couponVars.messages.error, "error");
    }
  }

  // Remove coupon via AJAX
  async function removeCoupon(code, couponElement) {
    log.info(`Starting remove coupon request for: ${code}`);

    try {
      const formData = new FormData();
      formData.append("action", "remove_coupon");
      formData.append("security", couponVars.nonce);
      formData.append("coupon_code", code);

      log.info("Sending AJAX request to remove coupon", {
        url: couponVars.ajaxUrl,
        code: code,
      });

      const response = await fetch(couponVars.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: new URLSearchParams(formData),
      });

      log.info(`AJAX response received, status: ${response.status}`);
      const data = await response.json();
      log.info("AJAX response data:", data);

      if (data.success) {
        log.info(`Coupon removed successfully: ${code}`);

        // Remove from tracking Set
        appliedCouponCodes.delete(code.toLowerCase());
        log.info("Removed coupon from tracking Set");

        // Remove from DOM
        couponElement.remove();
        log.info("Removed coupon element from DOM");

        showCouponMessage(data.data.message, "success");

        // Fetch updated order totals
        fetchAndRenderOrderTotals();
      } else {
        log.error(
          `Failed to remove coupon: ${
            data.data ? data.data.message : "Unknown error"
          }`
        );
        showCouponMessage(
          data.data?.message || couponVars.messages.error,
          "error"
        );
      }
    } catch (error) {
      log.error("Error removing coupon:", error);
      showCouponMessage(couponVars.messages.error, "error");
    }
  }

  // Fetch order totals via AJAX
  async function fetchAndRenderOrderTotals() {
    log.info("Fetching current order totals");

    try {
      // Show loading state
      const orderTotals = document.querySelector(".order-totals");
      if (orderTotals) {
        orderTotals.classList.add("loading");
      }

      const formData = new FormData();
      formData.append("action", "get_order_totals");
      formData.append("security", couponVars.nonce);

      const response = await fetch(couponVars.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: new URLSearchParams(formData),
      });

      const data = await response.json();
      log.info("Order totals response:", data);

      if (data.success) {
        renderOrderTotals(data.data);
      } else {
        log.error("Failed to fetch order totals:", data);
      }

      // Remove loading state
      if (orderTotals) {
        orderTotals.classList.remove("loading");
      }
    } catch (error) {
      log.error("Error fetching order totals:", error);
      // Remove loading state on error
      const orderTotals = document.querySelector(".order-totals");
      if (orderTotals) {
        orderTotals.classList.remove("loading");
      }
    }
  }

  // Render order totals from data
  function renderOrderTotals(totalsData) {
    log.info("Rendering order totals with data:", totalsData);

    const orderTotals = document.querySelector(".order-totals");
    if (!orderTotals) {
      log.warn("Order totals container not found");
      return;
    }

    // Clear existing content
    orderTotals.innerHTML = "";

    // Base price
    const basePrice = document.createElement("div");
    basePrice.className = "base-price";
    basePrice.innerHTML = `
      <span>${totalsData.base_price.label}</span>
      <span>${totalsData.base_price.formatted}</span>
    `;
    orderTotals.appendChild(basePrice);

    // VAT
    const vat = document.createElement("div");
    vat.className = "vat";
    vat.innerHTML = `
      <span>${totalsData.vat.label}</span>
      <span>${totalsData.vat.formatted}</span>
    `;
    orderTotals.appendChild(vat);

    // Subtotal
    const subtotal = document.createElement("div");
    subtotal.className = "subtotal";
    subtotal.innerHTML = `
      <span>${totalsData.subtotal.label}</span>
      <span>${totalsData.subtotal.formatted}</span>
    `;
    orderTotals.appendChild(subtotal);

    // SHIPPING FEE: Add the shipping row if it exists
    if (totalsData.shipping) {
      const shipping = document.createElement("div");
      shipping.className = "shipping"; // Add a class for styling
      shipping.innerHTML = `
            <span>${totalsData.shipping.label}</span>
            <span>${totalsData.shipping.formatted}</span>
        `;
      orderTotals.appendChild(shipping);
    }

    // First purchase discount (if applied)
    if (totalsData.first_purchase_discount.applied) {
      const fpDiscount = document.createElement("div");
      fpDiscount.className = "discount";
      fpDiscount.innerHTML = `
        <span>${totalsData.first_purchase_discount.label} (${totalsData.first_purchase_discount.percentage}%)</span>
        <span>-${totalsData.first_purchase_discount.formatted}</span>
      `;
      orderTotals.appendChild(fpDiscount);
    }

    // Coupon discount (if applied)
    if (totalsData.coupon_discount.applied) {
      const cpDiscount = document.createElement("div");
      cpDiscount.className = "coupon-discount";
      cpDiscount.innerHTML = `
        <span>${totalsData.coupon_discount.label}</span>
        <span>-${totalsData.coupon_discount.formatted}</span>
      `;
      orderTotals.appendChild(cpDiscount);
    }

    // Total discount (if applied)
    if (totalsData.total_discount.applied) {
      const totalDiscount = document.createElement("div");
      totalDiscount.className = "total-discount";
      totalDiscount.innerHTML = `
        <span>${totalsData.total_discount.label}</span>
        <span>-${totalsData.total_discount.formatted}</span>
      `;
      orderTotals.appendChild(totalDiscount);
    }

    // Final total
    const total = document.createElement("div");
    total.className = "total";
    total.innerHTML = `
      <span>${totalsData.final_total.label}</span>
      <span>${totalsData.final_total.formatted}</span>
    `;
    orderTotals.appendChild(total);
  }

  // NEW FUNCTION: Clear all coupons on the server side
  async function clearServerSideCoupons() {
    log.info("Clearing any server-side coupons");

    try {
      const formData = new FormData();
      formData.append("action", "clear_all_coupons");
      formData.append("security", couponVars.nonce);
      formData.append("post_order", "true");

      const response = await fetch(couponVars.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: new URLSearchParams(formData),
      });

      const data = await response.json();
      log.info("Server-side coupon clearing response:", data);
    } catch (error) {
      log.error("Error clearing server-side coupons:", error);
    }
  }

  // Helper function to format price
  function formatPrice(amount) {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
    }).format(amount);
  }

  // Helper function for translations (fallback to key if not available)
  function t(key) {
    return typeof window.t === "function" ? window.t(key) : key;
  }

  // Initialize the apply coupon button functionality
  applyButton.addEventListener("click", function () {
    const couponCode = couponInput.value.trim();
    if (!couponCode) {
      log.warn("Apply button clicked with empty coupon code");
      return;
    }

    log.info(`Apply button clicked with coupon code: ${couponCode}`);

    // Check if coupon is already applied (case insensitive)
    if (appliedCouponCodes.has(couponCode.toLowerCase())) {
      log.warn(`Coupon already applied: ${couponCode}`);
      showCouponMessage(
        couponVars.messages.alreadyApplied ||
          "This coupon has already been applied",
        "error"
      );
      return;
    }

    applyCoupon(couponCode);
  });

  // Log initialization complete
  log.info("Coupon handler initialized successfully");

  // Keep track of the current page and the applied coupons
  let isCheckoutPage = document.querySelector("form.checkout-form") !== null;

  // Initialize a function to store coupon state in session storage
  function storeCouponState() {
    // Don't store coupon state if we're in a post-order state
    if (shouldClearCouponData()) {
      log.info("Not storing coupons because we're in a post-order state");
      return;
    }

    log.info("Storing coupon state in session storage");
    sessionStorage.setItem(
      "checkout_applied_coupons",
      JSON.stringify(Array.from(appliedCouponCodes))
    );
    sessionStorage.setItem("checkout_page_visited", "true");
    log.info(
      `Stored ${
        appliedCouponCodes.size
      } coupons in session storage: ${JSON.stringify(
        Array.from(appliedCouponCodes)
      )}`
    );
  }

  // Add page visibility change handler to detect navigation away
  document.addEventListener("visibilitychange", function () {
    if (isCheckoutPage && document.visibilityState === "hidden") {
      log.info("Page visibility changed to hidden, storing coupon state");
      storeCouponState();
    }
  });

  // Add before unload handler for navigation
  window.addEventListener("beforeunload", function () {
    if (isCheckoutPage) {
      log.info("Page unloading, storing coupon state");
      storeCouponState();
    }
  });

  // Handle checkout form submission - clear coupon storage on successful checkout
  const checkoutForm = document.querySelector("form.checkout-form");
  if (checkoutForm) {
    checkoutForm.addEventListener("submit", function () {
      log.info("Checkout form submitted, marking order as pending");
      // Instead of clearing immediately, set a flag that we're in the process of checking out
      sessionStorage.setItem("checkout_in_progress", "true");
    });
  }

  // Check if we're on the checkout page and need to restore coupons
  if (isCheckoutPage) {
    // Don't restore coupons if we're in a post-order state
    if (shouldClearCouponData()) {
      log.info(
        "Post-order state detected, clearing storage and not restoring coupons"
      );

      // Clear all coupon-related storage
      sessionStorage.removeItem("checkout_applied_coupons");
      sessionStorage.removeItem("checkout_page_visited");
      sessionStorage.removeItem("order_completed");
      sessionStorage.removeItem("checkout_in_progress");

      // Also make an AJAX call to clear server-side coupons
      clearServerSideCoupons();
    }
    // Don't restore coupons if a checkout is in progress
    else if (sessionStorage.getItem("checkout_in_progress")) {
      log.info("Checkout in progress, not restoring coupons");

      // Clear the in-progress flag as we're now on the checkout page again
      sessionStorage.removeItem("checkout_in_progress");
    } else {
      const storedCoupons = sessionStorage.getItem("checkout_applied_coupons");

      if (storedCoupons) {
        try {
          const couponArray = JSON.parse(storedCoupons);
          log.info(`Found ${couponArray.length} stored coupons to restore`);

          // Check which coupons need to be applied (not already in the UI)
          const couponsToRestore = couponArray.filter(
            (code) => !appliedCouponCodes.has(code.toLowerCase())
          );

          if (couponsToRestore.length > 0) {
            log.info(
              `Restoring ${
                couponsToRestore.length
              } coupons: ${couponsToRestore.join(", ")}`
            );

            // Show a message that we're restoring coupons
            showCouponMessage(
              couponVars.messages.restoringCoupons ||
                "Restoring your applied coupons...",
              "info"
            );

            // Apply each coupon sequentially
            const applyStoredCoupons = async () => {
              for (const code of couponsToRestore) {
                log.info(`Auto-applying coupon: ${code}`);
                await applyCoupon(code);
                // Small delay to avoid overwhelming the server
                await new Promise((resolve) => setTimeout(resolve, 300));
              }
              showCouponMessage(
                couponVars.messages.couponsRestored ||
                  "Your coupons have been restored",
                "success"
              );
              // Refresh order totals after all coupons are applied
              fetchAndRenderOrderTotals();
            };
            applyStoredCoupons();
          }
        } catch (error) {
          log.error("Error parsing stored coupons:", error);
        }
      }
    }
  }

  // Check if returning to non-checkout page and clean up coupons if needed
  if (
    !isCheckoutPage &&
    sessionStorage.getItem("checkout_page_visited") === "true"
  ) {
    const storedCoupons = sessionStorage.getItem("checkout_applied_coupons");
    if (storedCoupons) {
      log.info("Left checkout page with applied coupons, cleaning up");
      // Call cleanup endpoint
      cleanupAppliedCoupons(JSON.parse(storedCoupons));
      // Clear the session storage
      sessionStorage.removeItem("checkout_applied_coupons");
      sessionStorage.removeItem("checkout_page_visited");
    }
  }

  // Function to cleanup applied coupons
  async function cleanupAppliedCoupons(coupons) {
    if (!Array.isArray(coupons) || coupons.length === 0) {
      return;
    }

    log.info(`Cleaning up ${coupons.length} coupons: ${coupons.join(", ")}`);

    try {
      const formData = new FormData();
      formData.append("action", "cleanup_coupons");
      formData.append("security", couponVars.nonce);
      formData.append("coupons", JSON.stringify(coupons));

      const response = await fetch(couponVars.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: new URLSearchParams(formData),
      });

      const data = await response.json();
      log.info("Cleanup response:", data);
    } catch (error) {
      log.error("Error during coupon cleanup:", error);
    }
  }
}
